import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { SettingService } from 'src/app/services/setting/setting.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { GeneralService } from 'src/app/services/general/general.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-cm-contacts',
  templateUrl: './cm-contacts.component.html',
  styleUrls: ['./cm-contacts.component.scss']
})
export class CmContactsComponent extends DestructorComponent implements OnInit {

  contactsForm: FormGroup;
  isLoading: boolean = false;
  settingId: string = '';
  selectedLogoFile: File | null = null;
  logoPreviewUrl: string | null = null;
  isLogoUploading: boolean = false;
  uploadedLogoUrl: string = '';
  selectedSignatureFile: File | null = null;
  signaturePreviewUrl: string | null = null;
  isSignatureUploading: boolean = false;
  uploadedSignatureUrl: string = '';

  constructor(
    private fb: FormBuilder,
    private _settingService: SettingService,
    private _globalStateService: GlobalStateService,
    private _generalService: GeneralService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadContactsData();
  }

  private initializeForm(): void {
    this.contactsForm = this.fb.group({
      english: this.fb.group({
        location_title: [''],
        company: [''],
        contact_person: [''],
        address_line_1: [''],
        address_line_2: [''],
        city: [''],
        state: [''],
        country: [''],
        zip: [''],
        telephone: [''],
        fax: [''],
        ip_phone: [''],
        email_1: [''],
        email_2: [''],
        website_1: [''],
        website_2: [''],
        whatsapp_number: [''],
        facebook: [''],
        google: [''],
        twitter: [''],
        youtube: [''],
        linkedin: [''],
        instagram: [''],
        comment: [''],
        priority: ['1', [Validators.required]]
      }),
      japanese: this.fb.group({
        location_title: [''],
        company: [''],
        contact_person: [''],
        address_line_1: [''],
        address_line_2: [''],
        city: [''],
        state: [''],
        country: [''],
        zip: [''],
        telephone: [''],
        fax: [''],
        ip_phone: [''],
        email_1: [''],
        email_2: [''],
        website_1: [''],
        website_2: [''],
        whatsapp_number: [''],
        facebook: [''],
        google: [''],
        twitter: [''],
        youtube: [''],
        linkedin: [''],
        instagram: [''],
        comment: [''],
        priority: ['1', [Validators.required]]
      })
    });
  }

  private loadContactsData(): void {
    this.subscriptions.push(
      this._globalStateService.GetSetting().subscribe((res: any) => {
        if (res) {
          this.settingId = res._id;

          // Check if contacts data exists
          if (res.contacts) {
            const englishData = res.contacts.english || {};
            const japaneseData = res.contacts.japanese || {};

            // Set priority for both forms if it exists in the main contacts object
            if (res.contacts.priority) {
              englishData.priority = res.contacts.priority;
              japaneseData.priority = res.contacts.priority;
            }

            this.contactsForm.patchValue({
              english: englishData,
              japanese: japaneseData
            });
          }

          // Handle logo
          if (res.logo) {
            this.logoPreviewUrl = res.logo;
            this.uploadedLogoUrl = res.logo;
            this.updateLogoPreview(res.logo);
          }

          // Handle signature
          if (res.signature) {
            this.signaturePreviewUrl = res.signature;
            this.uploadedSignatureUrl = res.signature;
            this.updateSignaturePreview(res.signature);
          }
        }
      })
    );
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          text: 'Please select a valid image file (PNG, JPG, JPEG)',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        Swal.fire({
          text: 'File size must be less than 5MB',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      this.selectedLogoFile = file;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.logoPreviewUrl = e.target.result;
        if (this.logoPreviewUrl) {
          this.updateLogoPreview(this.logoPreviewUrl);
        }
      };
      reader.readAsDataURL(file);

      // Upload the logo immediately
      this.uploadLogo(file);
    }
  }

  private uploadLogo(file: File): void {
    this.isLogoUploading = true;
    this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
      next: (res) => {
        const imageUrl = res.data.url;
        const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

        this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
          next: () => {
            this.isLogoUploading = false;
            this.uploadedLogoUrl = imageUrl;
            console.log('Logo uploaded successfully:', imageUrl);
          },
          error: (error) => {
            this.isLogoUploading = false;
            console.error('Error uploading logo to S3:', error);
            Swal.fire({
              text: "Error uploading logo. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        });
      },
      error: (error) => {
        this.isLogoUploading = false;
        console.error('Error getting presigned URL:', error);
        Swal.fire({
          text: "Error uploading logo. Please try again.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    });
  }

  private updateLogoPreview(imageUrl: string): void {
    const imageWrapper = document.querySelector('.logo-input-wrapper') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = `url(${imageUrl})`;
    }
  }

  onSignatureSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          text: 'Please select a valid image file (PNG, JPG, JPEG)',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        Swal.fire({
          text: 'File size must be less than 5MB',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      this.selectedSignatureFile = file;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.signaturePreviewUrl = e.target.result;
        if (this.signaturePreviewUrl) {
          this.updateSignaturePreview(this.signaturePreviewUrl);
        }
      };
      reader.readAsDataURL(file);

      // Upload the signature immediately
      this.uploadSignature(file);
    }
  }

  private uploadSignature(file: File): void {
    this.isSignatureUploading = true;
    this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
      next: (res) => {
        const imageUrl = res.data.url;
        const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

        this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
          next: () => {
            this.isSignatureUploading = false;
            this.uploadedSignatureUrl = imageUrl;
            console.log('Signature uploaded successfully:', imageUrl);
          },
          error: (error) => {
            this.isSignatureUploading = false;
            console.error('Error uploading signature to S3:', error);
            Swal.fire({
              text: "Error uploading signature. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        });
      },
      error: (error) => {
        this.isSignatureUploading = false;
        console.error('Error getting presigned URL:', error);
        Swal.fire({
          text: "Error uploading signature. Please try again.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    });
  }

  private updateSignaturePreview(imageUrl: string): void {
    const imageWrapper = document.querySelector('.signature-input-wrapper') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = `url(${imageUrl})`;
    }
  }

  onSubmit(): void {
    if (this.contactsForm.valid) {
      this.isLoading = true;

      const formData = this.contactsForm.getRawValue();

      const submitData: any = {
        contacts: {
          english: formData.english,
          japanese: formData.japanese,
          priority: formData.english.priority || formData.japanese.priority || '1'
        },
        logo: this.uploadedLogoUrl,
        signature: this.uploadedSignatureUrl
      };

      console.log('Submitting contacts data:', submitData);

      this.subscriptions.push(
        this._settingService.UpdateData(this.settingId, submitData).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Contacts updated successfully:', response);
            this._globalStateService.SetSetting();

            Swal.fire({
              text: "Contacts have been successfully updated!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error updating contacts:', error);
            Swal.fire({
              text: "Error updating contacts. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    }
  }
}
